import React, { useEffect, useRef, useState } from 'react';
import { Col, Row, Spin } from 'antd';
import InfiniteScroll from 'react-infinite-scroller';
import pluralize from 'pluralize';
import { isNil } from 'lodash';

import PartnerCard from '../PartnerCard';
import { getPartnerRequestsByStatusGroup } from 'redux/reducers/partnerSlice';
import { PartnerResponseModel } from 'api/backofficeApiClient';
import { partnerCreatedAtSelector } from 'redux/selectors/partnerSelector';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { PartnerSectionEnum } from '../enums/PartnerSectionEnum';
import { PartnerStatusGroupFilterEnum } from 'api/backofficeApiClient';

const noPartnersMessage = 'You do not have any partners created.';
const nopPendingPartnersMessage = 'You do not have any pending partners.';
const nopReadyForPaymentsMessage = 'You do not have any partners ready to pay.';

type Props = {
  partnersType?: PartnerSectionEnum;
};
export default function PartnersSection(props: Props) {
  const { partnersType } = props;
  const infiniteScrollParentRef = useRef(null);
  const [partners, setPartners] = useState<PartnerResponseModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const partnerCreatedAt = useAppSelector(partnerCreatedAtSelector);
  const dispatch = useAppDispatch();

  useEffect(() => {
    initInfinityScroll();
  }, [partnerCreatedAt]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setPartners([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const getStatusGroup = () => {
    if (isNil(partnersType)) return PartnerStatusGroupFilterEnum.All;

    return partnersType === PartnerSectionEnum.pending
      ? PartnerStatusGroupFilterEnum.Pending
      : PartnerStatusGroupFilterEnum.ReadyForPayments;
  };

  const onLoadMore = async (pageNumber: number) => {
    const statusGroup = getStatusGroup();

    const result = await dispatch(
      getPartnerRequestsByStatusGroup({ pageNumber, statusGroup }),
    );

    if (getPartnerRequestsByStatusGroup.fulfilled.match(result)) {
      const loadedPartners = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const newPartners = [...partners, ...loadedPartners];
      const hasMoreRows = newPartners.length < totalCount;

      setHasMore(hasMoreRows);
      setPartners(newPartners);
      setTotalCount(totalCount);
    } else {
      setHasMore(false);
    }

    setIsInitialDataLoaded(true);
  };

  const getCorrectMessage = () =>{
    if (isNil(partnersType)) return noPartnersMessage;

    return partnersType === PartnerSectionEnum.pending
      ? nopPendingPartnersMessage
      : nopReadyForPaymentsMessage;
  }

  return (
    <div ref={infiniteScrollParentRef} className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          {isInitialDataLoaded && (
            <>
              {!!totalCount && (
                <p className="page-total-count">
                  {pluralize('partner', totalCount, true)}
                </p>
              )}

              {!totalCount && (
                <span className="no-data-msg">{getCorrectMessage()}</span>
              )}
            </>
          )}

          {isVisible && (
            <InfiniteScroll
              pageStart={-1}
              loadMore={onLoadMore}
              hasMore={hasMore}
              loader={<Spin key={0} />}
              useWindow={false}
              getScrollParent={() => infiniteScrollParentRef.current}
            >
              <Row gutter={[20, 20]}>
                {partners.map((partner, index) => (
                  <Col
                    xs={24}
                    lg={12}
                    key={`${partner.id}-${index}`}
                    className="display-flex"
                  >
                    <PartnerCard
                      partner={partner}
                      isMemberCompanyNameClickable={false}
                    />
                  </Col>
                ))}
              </Row>
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
}
